import React, { useState, useRef, useEffect } from "react";
import { Bug, FolderSearch, AlertTriangle, ClipboardList, Search, X, Lightbulb, ArrowRightCircle, Play, Square, Code2, Waypoints } from "lucide-react";

// Left-aligned actions. analyze_cmd_output is still withheld (it needs command
// output pasted in, which has no home in this bar); search sits alongside the
// diagnostic actions. Feedback lives in the panel header, next
// to the profile.
//
// Retrieval actions first, then the two that reason over the conversation so
// far — an engineer looks for prior art before asking the copilot what to do
// next, and the order should read that way.
const PRIMARY_ACTIONS = [
  { action: "find_bugs", label: "Find Bugs", icon: Bug, accent: true },
  { action: "find_cases", label: "Find Cases", icon: FolderSearch, accent: true },
  { action: "active_incident", label: "Active Incident", icon: AlertTriangle, accent: true },
  { action: "possible_causes", label: "Possible Causes", icon: Lightbulb, accent: true },
  { action: "next_step", label: "Next Step", icon: ArrowRightCircle, accent: true },
  { action: "case_analysis", label: "Case Analysis", icon: ClipboardList, accent: true },
  { action: "code_triage", label: "Code Triage", icon: Code2, accent: true },
  { action: "graph_view", label: "Graph view", icon: Waypoints, accent: true },
];

// Graph view opens the diagnosis category as lights (brain/lights/GraphLightsView). It only shows data already on screen and
// starts no turn, so the turn locks (hydrating, streaming, closed, expired) do not apply: it is enabled exactly when the
// chat's diagnosis has a category.
export const GRAPH_VIEW_ACTION = "graph_view";
const GRAPH_VIEW_UNAVAILABLE = "Available once a diagnosis category is set";

// Root Cause Analysis pill is hidden (2026-08-13, user request). The whole
// feature is left wired — state, prompt, handler — so flipping this back to
// true restores it; with it off, rcaEnabled can never leave false and every
// call-context send stays a plain attach.
const SHOW_RCA_TOGGLE = false;

/**
 * ActionBar — compact row of quick-action buttons. Calls onAction(actionName)
 * for every button; disabled state is caller-controlled via `disabledActions`.
 * Case Analysis expands inline to a case search form; submit via onCaseAnalysisSubmit.
 */
const ActionBar = ({
  onAction,
  onCaseAnalysisSubmit,
  disabledActions = [],
  variant = "default",
  // Coarser than disabledActions: a whole-bar switch for states — like a
  // session still hydrating — where a click would target a transcript that
  // has not loaded yet.
  disabled = false,
  // Why the bar is inert, shown as the title on the dead buttons. Without it a
  // closed session just reads as six broken controls.
  disabledReason = "",
  bugCount = 0,
  caseCount = 0,
  searched = {},
  unseen = {},
  onOpenResults,
  // { action, message } | null — shown right below the button it's about,
  // not a shared header slot, so it reads as tied to what you just clicked.
  actionNotice = null,
  // Root Cause Analysis is a MODE, not an action: while it is on, every batch
  // of call context the engineer sends starts an RCA turn by itself. So it
  // toggles and stays lit, rather than firing once like its neighbours.
  rcaEnabled = false,
  onToggleRca,
  // The chat's differential diagnosis has a category (enables Graph view).
  graphViewAvailable = false,
}) => {
  const [caseOpen, setCaseOpen] = useState(false);
  const [caseQuery, setCaseQuery] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (caseOpen) inputRef.current?.focus();
  }, [caseOpen]);

  const isDisabled = (action) => (action === GRAPH_VIEW_ACTION
    ? !graphViewAvailable
    : disabled || disabledActions.includes(action));
  const titleFor = (action, label) => {
    if (!isDisabled(action)) return label;
    if (action === GRAPH_VIEW_ACTION) return GRAPH_VIEW_UNAVAILABLE;
    return disabledReason || label;
  };

  const submitCase = (e) => {
    e?.preventDefault?.();
    const q = caseQuery.trim();
    if (!q) return;
    onCaseAnalysisSubmit?.(q);
    setCaseQuery("");
    setCaseOpen(false);
  };

  const renderAction = ({ action, label, icon: Icon, accent }) => {
    if (action === "case_analysis") {
      if (caseOpen) {
        return (
          <form key={action} className="d5-case-search" onSubmit={submitCase}>
            <div className="d5-case-search-field">
              <Search size={11} strokeWidth={2} aria-hidden />
              <input
                ref={inputRef}
                value={caseQuery}
                onChange={(e) => setCaseQuery(e.target.value)}
                placeholder="Case number or ID created before Aug 17 2026"
                aria-label="Case number or ID created before Aug 17 2026"
                disabled={disabled}
              />
            </div>
            <button type="submit" className="d5-case-search-go" disabled={disabled}>
              Search
            </button>
            <button
              type="button"
              className="d5-case-search-cancel"
              onClick={() => {
                setCaseOpen(false);
                setCaseQuery("");
              }}
              aria-label="Cancel"
            >
              <X size={12} />
            </button>
          </form>
        );
      }

      return (
        <span key={action} className="d5-action-btn-wrap">
          <button
            type="button"
            disabled={isDisabled(action)}
            onClick={() => setCaseOpen(true)}
            className={`d5-action-btn${accent ? " d5-action-btn-primary" : ""}`}
            title={titleFor(action, label)}
          >
            <Icon size={11} strokeWidth={2} />
            <span>{label}</span>
          </button>
          {actionNotice?.action === action && (
            <span className="d5-action-btn-notice">{actionNotice.message}</span>
          )}
        </span>
      );
    }

    return (
      <span key={action} className="d5-action-btn-wrap">
        <button
          type="button"
          disabled={isDisabled(action)}
          onClick={() => onAction?.(action)}
          className={`d5-action-btn${accent ? " d5-action-btn-primary" : ""}`}
          title={titleFor(action, label)}
        >
          <Icon size={11} strokeWidth={2} />
          <span>{label}</span>
        </button>
        {actionNotice?.action === action && (
          <span className="d5-action-btn-notice">{actionNotice.message}</span>
        )}
      </span>
    );
  };

  // A chip appears once a background search has RUN, not once it found
  // something: "0 Bugs" is a result the user asked to see, and hiding it made a
  // completed search look like it never happened.
  // Not on the customer's bar either. These chips open ResultsPanel onto the
  // same INTERNAL records the Find Bugs / Find Cases buttons list, so leaving
  // them up in enterprise put a one-click door to Jira and Salesforce next to
  // the three buttons that were removed for exactly that reason. Display only:
  // the background search still RUNS on this surface and its results still
  // arrive — this hides the chips, it does not stop the fill.
  const results = variant === "enterprise" ? [] : [
    { key: "bugs", label: "Bug", count: bugCount, icon: Bug },
    { key: "cases", label: "Case", count: caseCount, icon: FolderSearch },
  ].filter((r) => r.count > 0 || searched[r.key]);

  // Not on the customer's bar. Every one of these lists INTERNAL records:
  // Salesforce cases, Jira tickets and a case id lookup. A customer must never
  // be offered them, let alone shown a result.
  //
  // active_incident is DELIBERATELY not in this set (2026-09-22, user request).
  // A Zscaler service incident is the one record here that is not internal — it
  // is the vendor's own published outage status, and a customer asking "is
  // something down right now?" is asking the question the incident feed exists
  // to answer. The other three stay hidden.
  //
  // code_triage is hidden too (Zv5.9.96 row 3): it opens the engineer's code
  // agent over our source tree. /code-agent is already agent-only in App.js;
  // the button was the one thing still putting it in front of a customer.
  //
  // graph_view too: it draws the diagnosis graph, and the diagnosis panel is
  // never shown on the customer's surface (BrainPopover enterprise).
  const ENTERPRISE_HIDDEN = new Set([
    "case_analysis", "find_cases", "find_bugs", "code_triage", GRAPH_VIEW_ACTION,
  ]);
  const actionsToShow = variant === "enterprise"
    ? PRIMARY_ACTIONS.filter(act => !ENTERPRISE_HIDDEN.has(act.action))
    : PRIMARY_ACTIONS;

  return (
    <div className="d5-action-bar">
      <div className="d5-action-group">
        {actionsToShow.map(renderAction)}
      </div>
      {/* Everything that belongs at the right edge lives in ONE container.
          Two siblings each carrying margin-left:auto splits the free space
          between them, which is what put the result chips in the middle of the
          row instead of at its end. */}
      <div className="d5-action-right">
      {SHOW_RCA_TOGGLE && onToggleRca && (
        <button
          type="button"
          disabled={disabled}
          className={`d5-rca-toggle${rcaEnabled ? " d5-rca-toggle-on" : ""}`}
          aria-pressed={rcaEnabled}
          title={rcaEnabled
            ? "Root Cause Analysis is live — every batch of call context starts an analysis. Click to stop."
            : "Start Root Cause Analysis — every batch of call context you send will start an analysis automatically"}
          onClick={onToggleRca}
        >
          {rcaEnabled
            ? <span className="d5-rca-dot" aria-hidden />
            : <Play size={11} strokeWidth={2.5} fill="currentColor" />}
          <span>{rcaEnabled ? "RCA live" : "Root Cause Analysis"}</span>
          {/* The stop affordance stays visible while live — the pill is the
              control, and a mode you cannot obviously leave is a trap. */}
          {rcaEnabled && <Square size={10} strokeWidth={2.5} fill="currentColor" />}
        </button>
      )}
      {results.length > 0 && (
        <div className="d5-action-group">
          {results.map(({ key, label, count, icon: Icon }) => (
            <button
              key={key}
              type="button"
              className={`d5-result-btn d5-result-btn-${key} ${unseen[key] ? "d5-result-btn-unseen" : ""}`}
              title={`View ${count} matching ${label.toLowerCase()}${count === 1 ? "" : "s"}${unseen[key] ? " (new)" : ""}`}
              onClick={() => onOpenResults?.(key)}
            >
              <Icon size={11} strokeWidth={2} />
              <span>
                {count} {label}
                {count === 1 ? "" : "s"}
              </span>
              {unseen[key] && <span className="d5-result-dot" aria-label="New results" />}
            </button>
          ))}
        </div>
      )}
      </div>
    </div>
  );
};

export default ActionBar;
